import * as sortBy from 'lodash/sortBy'
import { createSelector } from 'reselect'
import { StoreDispatch } from 'interfaces/component'
import MoneyflowTrackers, {
  MoneyflowTrackersStateProps,
  MoneyflowTrackersDispatchProps,
  MoneyflowTrackersOwnProps,
} from 'components/features/pfm/MoneyflowTrackers/MoneyflowTrackers'
import { toggleMoneyflowTrackerDetailsActionCreator } from 'actions/moneyflowActions'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { setFilterCategoryActionCreator, setTimeframeActionCreator } from 'actions/globalActions'
import { MoneyflowTyped, ExtendedMoneyflowTracker } from 'interfaces/moneyflow'
import { TimeframeTyped } from 'interfaces/global'
import { CategoryId } from 'interfaces/meta'
import { Guards } from 'interfaces/guards'
import { moneyflowTrackersGuard, memberMoneyflowBreakdownGuard } from 'guards/moneyflowGuards'
import { connectLoadable } from 'helpers/loadableHelpers'
import { metaMoneyflowCategoriesSelector } from 'selectors/moneyflowSelectors'
import { moneyflowAppTrackersSelector, moneyflowExtendedTrackersSelector } from 'selectors/moneyflowSelectors'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { timeframeSelector } from 'selectors/globalSelectors'
import { categorySelector } from 'selectors/globalSelectors'

const sortBySeverity = ({ severity }: ExtendedMoneyflowTracker) => {
  switch (severity) {
    case 'danger':
      return 0

    case 'warning':
      return 1

    default:
      return 2
  }
}

const sortByValue = (tracker: ExtendedMoneyflowTracker): number => {
  const { type, projection, limit, current, severity } = tracker
  if (type === 'spending') {
    switch (severity) {
      case 'danger':
        return limit - current

      case 'warning':
        return limit - projection

      default:
        return limit - current
    }
  }

  if (type === 'income') {
    switch (severity) {
      case 'warning':
        return projection - limit

      default:
        return current - limit
    }
  }

  return 0
}

const createMapStateToProps = () =>
  createSelector(
    metaMoneyflowCategoriesSelector,
    moneyflowExtendedTrackersSelector,
    moneyflowAppTrackersSelector,
    timeframeSelector,
    categorySelector,
    (categories, trackers, { expandedCategoryId }, selectedTimeframe, selectedCategory) => {
      if (!trackers) {
        throw createForgottenGuardError('MoneyflowTrackersModuleContainer', 'moneyflowTrackersGuard')
      }

      return {
        expandedCategoryId,
        trackers: sortBy(trackers, [sortBySeverity, sortByValue]),
        categories,
        selectedTimeframe,
        selectedCategory,
      }
    }
  )


const mapDispatchToProps = (dispatch: StoreDispatch, { type, timeframe }: MoneyflowTrackersOwnProps) => ({
  showTrackerPane: (categoryId: CategoryId) => dispatch(
    modifyFloatingPaneActionCreator('tracker', {
      type: 'tracker',
      payload: {
        category: categoryId,
        type,
        timeframe,
      },
    })
  ),
  toggleTrackerDetails: (categoryId: CategoryId) => dispatch(
    toggleMoneyflowTrackerDetailsActionCreator(type, categoryId)
  ),
  setFilterCategory: (categoryId: CategoryId) => dispatch(
    setFilterCategoryActionCreator(type, categoryId)
  ),
  setTimeframeCurrentMonth: () => dispatch(
    setTimeframeActionCreator(type, 'current-month'),
  ),
})


const guards: Guards<MoneyflowTyped & TimeframeTyped> = [
  moneyflowTrackersGuard,
  memberMoneyflowBreakdownGuard,
]

const MoneyflowTrackersContainer = connectLoadable<
  MoneyflowTrackersStateProps,
  MoneyflowTrackersDispatchProps,
  MoneyflowTrackersOwnProps
>(guards)(createMapStateToProps, mapDispatchToProps)(MoneyflowTrackers)
export default MoneyflowTrackersContainer
